import { useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { Typography, theme } from 'antd'

/**
 * Ustunli grafik — bir yoki bir necha qatlam (stacked).
 *
 * <h2>Nega kutubxona emas, SVG</h2>
 * Panelning bog'liqliklarida chizish kutubxonasi YO'Q va uni qo'shish
 * alohida qaror (`SubscriberTrafficPanel` izohi): recharts + d3 bog'liqligi
 * bilan birga ~400 KB, holbuki bu yerda kerak bo'lgani — kunlar bo'yicha
 * ustunlar va sichqoncha ostidagi qiymat. `ActiveSessionsTrend` allaqachon
 * shu yo'ldan boradi, ya'ni bu naqsh loyihada yangi emas.
 *
 * <h2>⚠️ Ranglar CHAQIRUVCHIDAN, qotirilmaydi</h2>
 * Chaqiruvchi ularni `theme.useToken()` dan oladi. Qotirilgan hex qorong'i
 * mavzuda kontrastini yo'qotardi — mahsulot esa ishlaydigan qorong'i mavzu
 * bilan keladi.
 *
 * <h2>⚠️ NOL QIYMAT — KO'RINADIGAN, lekin bo'sh ustun</h2>
 * Trafiksiz kun grafikdan TUSHIB QOLMAYDI: u nol balandlikdagi ustun
 * sifatida o'z joyida turadi. Aks holda "shu kuni trafik bo'lmagan" va "shu
 * kun umuman yo'q" bir xil ko'rinardi, ya'ni uzilishni sezish imkonsiz
 * bo'lardi.
 */
export interface ChartSeries {
  key: string
  label: string
  color: string
}

export interface ChartPoint {
  key: string
  /** X o'qidagi va tooltipdagi yozuv — SERVERDA tayyorlangan matn */
  label: string
  values: Readonly<Record<string, number>>
}

interface BarSeriesChartProps {
  series: readonly ChartSeries[]
  points: readonly ChartPoint[]
  /** Qiymatni matnga o'girish (bayt yoki pul) */
  format: (value: number) => string
  height?: number
  ariaLabel: string
  /** Jami satri tooltipda ko'rsatilsinmi — bitta qatlamda ortiqcha */
  showTotal?: boolean
  totalLabel?: string
}

const PAD_TOP = 8
const PAD_BOTTOM = 18

export function BarSeriesChart({
  series,
  points,
  format,
  height = 180,
  ariaLabel,
  showTotal = true,
  totalLabel,
}: BarSeriesChartProps) {
  const { token } = theme.useToken()
  const [hover, setHover] = useState<number | null>(null)

  const max = useMemo(() => {
    const totals = points.map((p) => series.reduce((sum, s) => sum + (p.values[s.key] ?? 0), 0))
    // ⚠️ Nol bo'lsa 1 ga almashtiriladi: 0 ga bo'lish `NaN` beradi va butun
    // grafik chizilmay qolardi — bo'sh kunlar to'plami odatiy hol.
    return Math.max(1, ...totals)
  }, [points, series])

  if (points.length === 0) return null

  const plotHeight = height - PAD_TOP - PAD_BOTTOM
  const slot = 100 / points.length
  // Ustunlar orasidagi bo'shliq nisbatda — ustunlar soni o'zgarganda
  // ular bir-biriga yopishib qolmasin
  const barWidth = Math.max(slot * 0.55, 0.6)

  const hovered = hover !== null ? points[hover] : null
  const hoveredTotal = hovered
    ? series.reduce((sum, s) => sum + (hovered.values[s.key] ?? 0), 0)
    : 0

  const handleMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = (e.clientX - rect.left) / rect.width
    const index = Math.floor(ratio * points.length)
    setHover(Math.max(0, Math.min(points.length - 1, index)))
  }

  // Faqat birinchi, o'rta va oxirgi yozuv chiziladi: 30 kunlik oynada
  // hammasi bir-birining ustiga tushardi
  const tickIndexes = new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])

  return (
    <div style={{ position: 'relative' }} onMouseMove={handleMove} onMouseLeave={() => setHover(null)}>
      <svg
        viewBox={`0 0 100 ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
      >
        {/*
          ⚠️ ASOS CHIZIG'I — u bo'lmasa NOL kunlar KO'RINMAY QOLADI.

          Server faqat ma'lumoti bor kunlarni qaytaradi va oyna nollar
          bilan to'ldiriladi (`TrafficRevenuePanel`). Nol balandlikdagi
          ustun esa chizilmaydi — natijada 14 kunlik oynada uchta ustun
          o'ng chekkada osilib turardi va grafik "buzuq" ko'rinardi. Asos
          chizig'i o'sha kunlar mavjud, faqat qiymati nol ekanini
          ko'rsatadi.
        */}
        <line
          x1={0}
          x2={100}
          y1={PAD_TOP + plotHeight}
          y2={PAD_TOP + plotHeight}
          stroke={token.colorBorder}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        {points.map((point, index) => {
          let cursor = PAD_TOP + plotHeight
          const x = index * slot + (slot - barWidth) / 2
          return (
            <g key={point.key}>
              {/* Sichqoncha uchun ko'rinmas maydon — past ustunni ham tutadi */}
              <rect
                x={index * slot}
                y={PAD_TOP}
                width={slot}
                height={plotHeight}
                fill={hover === index ? token.colorFillTertiary : 'transparent'}
              />
              {series.map((s) => {
                const value = point.values[s.key] ?? 0
                const h = (value / max) * plotHeight
                cursor -= h
                return (
                  <rect
                    key={s.key}
                    x={x}
                    y={cursor}
                    width={barWidth}
                    height={h}
                    fill={s.color}
                    opacity={hover === null || hover === index ? 1 : 0.45}
                  />
                )
              })}
            </g>
          )
        })}
      </svg>

      {/* X o'qi yozuvlari — SVG ichida emas: `preserveAspectRatio="none"`
          matnni cho'zib yuborardi */}
      <div style={{ display: 'flex', marginTop: -PAD_BOTTOM + 2 }}>
        {points.map((point, index) => (
          <div
            key={point.key}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 10,
              color: token.colorTextTertiary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {tickIndexes.has(index) ? point.label : ''}
          </div>
        ))}
      </div>

      {hovered ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: token.colorBgElevated,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadiusSM,
            padding: '6px 10px',
            fontSize: 12,
            boxShadow: token.boxShadowSecondary,
            pointerEvents: 'none',
            minWidth: 150,
          }}
        >
          <Typography.Text strong style={{ fontSize: 12, display: 'block' }}>
            {hovered.label}
          </Typography.Text>
          {series.map((s) => (
            <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: s.color,
                    marginInlineEnd: 5,
                  }}
                />
                {s.label}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 11 }}>
                {format(hovered.values[s.key] ?? 0)}
              </Typography.Text>
            </div>
          ))}
          {showTotal && series.length > 1 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                marginTop: 4,
                paddingTop: 4,
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {totalLabel}
              </Typography.Text>
              <Typography.Text strong style={{ fontSize: 11 }}>
                {format(hoveredTotal)}
              </Typography.Text>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
